import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "../baseQuery";
import {
  AddNewRole,
  AllUsersParams,
  OrderedUsers,
  PermissionBlock,
  SetNewPasswordRequest,
  SetNewPasswordResponse,
  UpdateRecipient,
  UpdateUseritem,
  User,
  UserItem,
  UsersResponse,
  FortnoxConnectionResponse,
  FortnoxLoginUrlResponse,
} from "./models";

export const usersApi = createApi({
  baseQuery,
  reducerPath: "usersApi",
  tagTypes: ["Users", "Roles"],
  endpoints: (builder) => ({
    getUsers: builder.query<UsersResponse, AllUsersParams>({
      query: ({
        ordering,
        limit,
        offset,
        expand,
        username__icontains,
        first_name__icontains,
        last_name__icontains,
        email__icontains,
        is_superuser,
        roles__name__icontains,
      }) => ({
        url: "/api/v1/users/",
        method: "GET",
        params: {
          ordering,
          limit,
          offset,
          expand,
          username__icontains,
          first_name__icontains,
          last_name__icontains,
          email__icontains,
          is_superuser,
          roles__name__icontains,
        },
      }),
      providesTags: ["Users"],
    }),
    getFilteredUsers: builder.query<UserItem[], OrderedUsers>({
      query: ({ ordering }) => ({
        url: `/api/v1/users/`,
        method: "GET",
        params: {
          ordering,
        },
      }),
      providesTags: ["Users"],
    }),
    getIndividualUser: builder.query<UserItem, number>({
      query: (id) => ({
        url: `/api/v1/users/${id}`,
        method: "GET",
      }),
    }),
    getLoggedUser: builder.query<UserItem, void>({
      query: () => ({
        url: "/api/v1/users/me/",
        method: "GET",
      }),
    }),
    createNewUser: builder.mutation<UpdateUseritem, UpdateUseritem>({
      query: (body) => ({
        url: `/api/v1/users/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Users"],
    }),
    updateUser: builder.mutation<UpdateUseritem, UpdateUseritem>({
      query: (body) => ({
        url: `/api/v1/users/${body.id}/`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Users"],
    }),
    deleteUser: builder.mutation<void, UserItem>({
      query: (body) => ({
        url: `/api/v1/users/${body.id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Users"],
    }),
    setNewPassword: builder.mutation<
      SetNewPasswordResponse,
      SetNewPasswordRequest
    >({
      query: ({ userId, newPassword }) => ({
        url: `/api/v1/users/${userId}/set_new_password/`,
        method: "POST",
        body: { new_password: newPassword },
      }),
    }),
    updateRoleById: builder.mutation<AddNewRole, AddNewRole>({
      query: (body) => ({
        url: `/api/v1/roles/${body.id}/`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Roles"],
    }),
    getPermissionBlocks: builder.query<PermissionBlock[], void>({
      query: () => "/api/v1/roles/all_permission_blocks/",
    }),
    getRolesForTable: builder.query<AddNewRole[], any>({
      query: (params) => ({
        url: "/api/v1/roles/",
        method: "GET",
        params,
      }),
    }),
    getUserPersonalInfo: builder.query<User, void>({
      query: () => "/api/v1/users/me",
    }),
    addNewRole: builder.mutation<AddNewRole, AddNewRole>({
      query: ({ name, permission_blocks }) => ({
        url: `/api/v1/roles/`,
        method: "POST",
        body: {
          name,
          permission_blocks,
        },
      }),
      invalidatesTags: ["Roles"],
    }),
    getRecipientsList: builder.query<UpdateRecipient, void>({
      query: () => ({
        url: "/api/v1/configurations/t1_recipient_list/",
        method: "GET",
      }),
    }),

    updateRecipientsList: builder.mutation<any, UpdateRecipient>({
      query: (body) => ({
        url: "/api/v1/configurations/t1_recipient_list/",
        method: "PUT",
        body,
      }),
    }),
    uploadUserImage: builder.mutation<
      void,
      { id: number; file: File; format?: string }
    >({
      query: ({ id, file, format }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/api/v1/users/${id}/image/`,
          method: "POST",
          body: formData,
          params: format ? { format } : undefined,
        };
      },
      invalidatesTags: ["Users"],
    }),
    checkFortnoxConnection: builder.query<FortnoxConnectionResponse, void>({
      query: () => ({
        url: "/api/v1/users/check_fortnox_connection/",
        method: "GET",
      }),
    }),
    getLoginFortnoxUrl: builder.query<FortnoxLoginUrlResponse, void>({
      query: () => ({
        url: "/api/v1/users/get_login_fortnox_url/",
        method: "GET",
      }),
    }),
  }),
});

export const {
  useUpdateRoleByIdMutation,
  useGetUserPersonalInfoQuery,
  useGetUsersQuery,
  useGetIndividualUserQuery,
  useGetLoggedUserQuery,
  useCreateNewUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetFilteredUsersQuery,
  useSetNewPasswordMutation,
  useGetPermissionBlocksQuery,
  useGetRolesForTableQuery,
  useAddNewRoleMutation,
  useGetRecipientsListQuery,
  useUpdateRecipientsListMutation,
  useUploadUserImageMutation,
  useCheckFortnoxConnectionQuery,
  useGetLoginFortnoxUrlQuery,
} = usersApi;
